<!--
@component
TeamsMenu

See markup below.
-->

<script lang="ts">
  import { appState } from '$lib/stores/index.svelte';
  import Flag from '$lib/components/common/Flag/index.svelte';
  import getTeamPath from '$utils/getTeamPath';
  import { slide } from 'svelte/transition';

  // Breakpoint -> column count mapping (ascending by max width).
  // First entry whose `maxWidth` exceeds windowWidth wins.
  const COLUMN_BREAKPOINTS: ReadonlyArray<{ maxWidth: number; cols: number }> =
    [
      { maxWidth: 420, cols: 1 },
      { maxWidth: 650, cols: 2 },
      { maxWidth: 850, cols: 3 },
      { maxWidth: 1300, cols: 4 },
      { maxWidth: Infinity, cols: 6 },
    ];

  /**
   * Calculates the grid layout for the menu.
   * @param teamLength - The number of teams.
   * @param windowWidth - The current viewport width.
   * @returns Rows and columns for the grid layout.
   */
  const calculateMenuGrid = (teamLength: number, windowWidth: number) => {
    const cols =
      COLUMN_BREAKPOINTS.find((bp) => windowWidth < bp.maxWidth)?.cols ?? 4;
    const rows = Math.ceil(teamLength / cols);
    return { rows, cols };
  };

  /** Grid layout, recomputed when teams or window width change. */
  const grid = $derived(
    calculateMenuGrid(appState.teams.length, appState.windowWidth)
  );

  /** Length (in chars) of the longest team short name — used for sizing links. */
  const wordLen = $derived(
    appState.teams.length === 0 ?
      0
    : Math.max(...appState.teams.map((t) => t.shortName.length))
  );

  // Props
  let {
    showDropdownMenu = $bindable(false),
    active = $bindable(-1),
  }: {
    showDropdownMenu?: boolean;
    active?: number;
  } = $props();

  /** Reference to the dropdown menu element. */
  let dropdownMenu: HTMLDivElement;

  /** Focuses the link at the given index, if it exists. */
  const focusItem = (index: number) => {
    const item = dropdownMenu?.children[index];
    if (item instanceof HTMLElement) item.focus();
  };

  /**
   * Handles keyboard navigation within the open dropdown.
   * Only active while the menu is shown.
   */
  const handleKeydown = (e: KeyboardEvent) => {
    if (!showDropdownMenu || !dropdownMenu) return;

    const lastIndex = dropdownMenu.children.length - 1;

    switch (e.key) {
      case 'ArrowDown':
        if (active < lastIndex) {
          active++;
          focusItem(active);
          e.preventDefault();
        }
        break;
      case 'ArrowUp':
        if (active > 0) {
          active--;
          focusItem(active);
          e.preventDefault();
        }
        break;
      case 'Home':
        active = 0;
        focusItem(active);
        e.preventDefault();
        break;
      case 'End':
        active = lastIndex;
        focusItem(active);
        e.preventDefault();
        break;
      case 'Escape':
        showDropdownMenu = false;
        e.preventDefault();
        break;
    }
  };

  /**
   * Closes the menu when a click occurs outside of it.
   * Clicks inside the menu (e.g. on a link) are handled by their own
   * `onclick` handlers, which is where we want to keep that logic.
   */
  const handleWindowClick = (e: MouseEvent) => {
    if (!showDropdownMenu) return;
    const target = e.target;
    if (target instanceof Node && dropdownMenu?.contains(target)) return;
    showDropdownMenu = false;
  };
</script>

<svelte:window onkeydown={handleKeydown} onclick={handleWindowClick} />

<div
  class="dropdown-menu relative fpx-5 fpy-3"
  class:show={showDropdownMenu}
  class:loading={appState.isLoading}
  style="--rows:{grid.rows}; --cols:{grid.cols}; --wordLen:{wordLen}"
  role="menu"
  aria-hidden={!showDropdownMenu}
  bind:this={dropdownMenu}
  transition:slide|local={{ axis: 'y', duration: 350 }}
>
  {#each appState.teams as team (team.slug)}
    {@const isActive = appState.activeCountry[0]?.slug === team.slug}
    <a
      class="team-link flex justify-left mx-auto tracking-wide items-center text-sm px-2 py-4"
      class:activeCountry={isActive}
      role="menuitem"
      aria-current={isActive ? 'page' : undefined}
      href={getTeamPath(team)?.url ?? '#'}
      onclick={() => {
        showDropdownMenu = false;
      }}
    >
      <span class="icon mr-1">
        <Flag country={team.name} width="var(--theme-font-size-lg)" circle />
      </span>

      {team.shortName}
    </a>
  {/each}
</div>

<style lang="scss">
  @use '../../../../styles/mixins' as mixins;

  .dropdown-menu {
    top: 0;
    border-bottom-left-radius: var(--theme-style-border-radius);
    border-bottom-right-radius: var(--theme-style-border-radius);
    display: none;

    -webkit-user-drag: none;

    &.show {
      // max-width: mixins.$column-width-wider;
      max-height: calc(100svh - 4.5 * var(--nav-height));
      overflow-y: scroll;
      @include mixins.hide-scrollbar;

      display: grid;
      gap: clamp(0.31rem, 0.31rem + 0vw, 0.31rem);

      justify-content: center;
      grid-auto-flow: column;
      grid-template-columns: repeat(var(--cols), 1fr);
      grid-template-rows: repeat(var(--rows), auto);

      @media (max-width: 1000px) {
        gap: clamp(0.56rem, 0.52rem + 0.21vw, 0.69rem);
      }
    }
  }

  .icon {
    // `Flag` ships the inlined base64 fallback PNG until the real flag
    // decodes; no need for another network `UNKNOWN.png` behind it.
    background-color: var(--theme-colour-tan-grey);
    width: var(--theme-font-size-lg);
    border-radius: 50%;
    outline: 1px solid var(--theme-colour-white);
    aspect-ratio: 1;
  }

  .team-link {
    text-decoration: none;
    height: var(--theme-font-size-2xl);
    border-radius: var(--theme-style-border-radius-pill);
    width: calc(0.65rem * var(--wordLen) + var(--theme-font-size-lg));
    @media (max-width: 900px) {
      width: calc(0.6rem * var(--wordLen) + var(--theme-font-size-lg));
    }
    @media (max-width: 400px) {
      width: calc(0.6rem * var(--wordLen) + var(--theme-font-size-lg));
    }

    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &:hover,
    &:focus,
    &:active,
    &.active,
    &.activeCountry {
      @include mixins.font-medium;
      color: var(--theme-colour-logo);
      background-color: var(--theme-colour-white);
      outline: 1px solid var(--theme-colour-logo, #d0d0d0);

      @include mixins.hard-shadow();
    }

    &:active,
    &.active,
    &.activeCountry {
      outline-width: 2px;
    }
  }
</style>
